import React, { useState, useEffect } from 'react';

const API = 'https://tcio-api.onrender.com';

// Design tokens - ratified CRM facelift language (2026-08-26)
const T = {
  canvas: '#F4F8FB', surface: '#FFFFFF', text: '#22303E', sub: '#5D6B7A',
  blue: '#0878E8', border: '#D8E3EE', red: '#B91C1C', amber: '#B45309',
  green: '#15803D', navy: '#0A2239',
};

const DISMISS_REASONS = [
  'Wrong number', 'Spam / robocall', 'Not seeking treatment', 'Hung up',
  'Job seeker', 'Vendor / solicitation', 'Other'
];

const fmtDuration = (s) => {
  if (!s) return '0:00';
  const m = Math.floor(s / 60); const sec = s % 60;
  return m + ':' + String(sec).padStart(2, '0');
};
const fmtTime = (t) => {
  if (!t) return '';
  const d = new Date(t); const mins = Math.floor((Date.now() - d) / 60000);
  if (mins < 60) return mins + 'm ago';
  if (mins < 1440) return Math.floor(mins / 60) + 'h ago';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};
const fmtClock = (t) => t ? new Date(t).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '';

// Real AI classification buckets - mapped from call_scores.call_type ONLY. Never fabricated.
const BUCKETS = {
  NEW_SEEKER:            { label: 'Likely Admission',    color: T.blue,  sub: 'High intent signals' },
  CURRENT_CLIENT:        { label: 'Existing Client',     color: T.amber, sub: 'Client calling in' },
  CURRENT_CLIENT_FAMILY: { label: 'Client Family',       color: T.amber, sub: 'Family of client' },
  OTHER_BUSINESS:        { label: 'Business / Referral', color: '#6D28D9', sub: 'Providers, professionals' },
  JUNK:                  { label: 'Low Priority',        color: T.sub,   sub: 'Spam, vendors' },
  TOO_SHORT:             { label: 'Low Priority',        color: T.sub,   sub: 'Hangup / fragment' },
};
const clsInfo = (c) => {
  if (!c.call_type) return { label: 'Awaiting AI', color: '#94A3B8' };
  return BUCKETS[c.call_type] || { label: c.call_type, color: T.sub };
};

function useIsMobile() {
  const [m, setM] = useState(typeof window !== 'undefined' && window.innerWidth < 768);
  useEffect(() => {
    const h = () => setM(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return m;
}

const STATUS_PILL = {
  needs_triage: { label: 'Needs Triage', bg: '#FEF3C7', fg: '#92400E' },
  likely_junk:  { label: 'Likely Junk',  bg: '#F1F5F9', fg: T.sub },
  qualified:    { label: 'Qualified',    bg: '#DCFCE7', fg: '#166534' },
  dismissed:    { label: 'Dismissed',    bg: '#F1F5F9', fg: '#94A3B8' },
  missed:       { label: 'Missed',       bg: '#FEE2E2', fg: T.red },
};
const statusPill = (st) => STATUS_PILL[st] || { label: st, bg: '#F1F5F9', fg: T.sub };

export default function Calls({ user }) {
  const isMobile = useIsMobile();
  const [calls, setCalls] = useState([]);
  const [counts, setCounts] = useState({});
  const [today, setToday] = useState({});
  const [scope, setScope] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('needs_triage');
  const [facilities, setFacilities] = useState([]);
  const [facFilter, setFacFilter] = useState('all');
  const [srcFilter, setSrcFilter] = useState('all');
  const [dirFilter, setDirFilter] = useState('all');
  const [clsFilter, setClsFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [sortMode, setSortMode] = useState('recommended');
  const [expanded, setExpanded] = useState(null);
  const [dismissOpen, setDismissOpen] = useState(false);
  const [qualifyOpen, setQualifyOpen] = useState(false);
  const [playing, setPlaying] = useState(null);
  const [busy, setBusy] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [reviewCount, setReviewCount] = useState(0);
  const [fullT, setFullT] = useState({});
  const [showFullT, setShowFullT] = useState(false);
  const [search, setSearch] = useState('');
  const [xhits, setXhits] = useState([]);

  const headers = { Authorization: 'Bearer ' + user.token };

  useEffect(() => {
    if (!search || search.trim().length < 3) { setXhits([]); return; }
    const h = setTimeout(() => {
      fetch(API + '/api/calls/search?q=' + encodeURIComponent(search.trim()), { headers })
        .then(r => r.json()).then(d => setXhits(d.ok ? d.data : [])).catch(() => setXhits([]));
    }, 300);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // silent = the 20-second background refresh: no Loading flash, and what the page already loaded for a call (its audio) is kept (Ken eb75480f).
  const load = async (silent) => {
    if (!silent) setLoading(true);
    try {
      const fq = facFilter !== 'all' ? '&facilityId=' + facFilter : '';
      const dmap = { today: 1, week: 7, month: 30, year: 365 };
      const dq = dmap[dateFilter] ? '&days=' + dmap[dateFilter] : '';
      const r = await fetch(API + '/api/calls?status=' + tab + fq + dq, { headers });
      const d = await r.json();
      if (d.ok) {
        setCalls(prev => {
          const keep = {};
          (prev || []).forEach(c => { const x = {}; Object.keys(c).forEach(k => { if (k[0] === '_') x[k] = c[k]; }); if (Object.keys(x).length) keep[c.id] = x; });
          return (d.data || []).map(c => keep[c.id] ? { ...c, ...keep[c.id] } : c);
        });
        setCounts(d.counts);
        if (d.today) setToday(d.today);
        if (d.scope) setScope(d.scope);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => {
    fetch(API + '/api/facilities', { headers })
      .then(r => r.json()).then(d => d.ok && setFacilities(d.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { load(); setPage(1); setExpanded(null); /* eslint-disable-next-line */ }, [tab, facFilter, dateFilter]);
  useEffect(() => {
    const fq = facFilter !== 'all' ? '?facilityId=' + facFilter : '';
    fetch(API + '/api/calls/review' + fq, { headers })
      .then(r => r.json()).then(d => d.ok && setReviewCount(d.count)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, facFilter]);
  useEffect(() => {
    const t = setInterval(() => { load(true); }, 20000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, facFilter, dateFilter]);

  const doQualify = async (call, facilityId, loc) => {
    setBusy(true);
    try {
      const r = await fetch(API + '/api/calls/' + call.id + '/qualify', {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ facility_id: facilityId, loc_requested: loc })
      });
      const d = await r.json();
      if (d.ok) { setQualifyOpen(false); setExpanded(null); load(); }
      else alert('Error: ' + d.error);
    } catch (e) { alert(e.message); }
    setBusy(false);
  };

  const doDismiss = async (call, reason) => {
    setBusy(true);
    try {
      const r = await fetch(API + '/api/calls/' + call.id + '/dismiss', {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason })
      });
      if (r.status === 401) { alert('Your session expired - please log in again.'); setBusy(false); return; }
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d.ok === false) { alert('Dismiss failed: ' + (d.error || r.status)); setBusy(false); return; }
      setDismissOpen(false); setExpanded(null); load();
    } catch (e) { alert('Dismiss failed: ' + e.message); }
    setBusy(false);
  };

  const doUndismiss = async (call) => {
    try {
      const r = await fetch(API + '/api/calls/' + call.id + '/undismiss', { method: 'POST', headers });
      if (r.status === 401) { alert('Your session expired - please log in again.'); return; }
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d.ok === false) { alert('Undo failed: ' + (d.error || r.status)); return; }
      load();
    } catch (e) { alert('Undo failed: ' + e.message); }
  };

  const playRecording = async (call) => {
    if (playing === call.id) { setPlaying(null); return; }
    try {
      const r = await fetch(API + '/api/calls/' + call.id + '/recording', { headers });
      const d = await r.json();
      if (d.ok) { setPlaying(call.id); setCalls(cs => cs.map(c => c.id === call.id ? { ...c, _audio: d.url } : c)); }
      else alert('No recording available');
    } catch (e) { alert(e.message); }
  };

  const loadFullTranscript = async (call) => {
    if (fullT[call.id]) { setShowFullT(v => !v); return; }
    try {
      const r = await fetch(API + '/api/transcripts/call/' + call.callrail_id, { headers });
      const d = await r.json();
      if (!r.ok || !d.ok) { alert('Could not load transcript: ' + ((d && d.error) || ('HTTP ' + r.status))); return; }
      if (!d.found || !d.data || !d.data.transcript) {
        alert(d.data && d.data.transcript_status === 'error'
          ? 'Transcription failed for this call: ' + (d.data.transcript_error || 'unknown error')
          : 'No transcript exists for this call yet - transcription may still be running.');
        return;
      }
      const t = d.data;
      const short = (t.word_count || 0) < 30;
      const text = t.transcript + (short
        ? '\n\n[This is the entire transcript - the call was only ' + (t.duration_seconds || '?') + ' seconds with ' + t.word_count + ' words spoken.]'
        : '');
      setFullT(f => ({ ...f, [call.id]: text })); setShowFullT(true);
    } catch (e) { alert('Could not load transcript: ' + e.message); }
  };

  // ---- client-side filtering / sorting / pagination ----
  const _q = search.toLowerCase().replace(/[^a-z0-9]/g, '');
  const _qr = search.toLowerCase();
  const clsOk = (c) => {
    if (clsFilter === 'all') return true;
    if (clsFilter === 'unscored') return !c.call_type;
    if (clsFilter === 'LOW') return c.call_type === 'JUNK' || c.call_type === 'TOO_SHORT';
    if (clsFilter === 'EXISTING') return c.call_type === 'CURRENT_CLIENT' || c.call_type === 'CURRENT_CLIENT_FAMILY';
    return c.call_type === clsFilter;
  };
  const shown = calls.filter(c =>
    (!search ||
      (c.caller_phone || '').replace(/\D/g, '').includes(_q) ||
      (c.caller_city || '').toLowerCase().includes(_qr) ||
      (c.source || '').toLowerCase().includes(_qr) ||
      (c.transcript_preview || '').toLowerCase().includes(_qr))
    && (srcFilter === 'all' || c.source === srcFilter)
    && (dirFilter === 'all' || c.direction === dirFilter)
    && clsOk(c)
  );
  const sorted = [...shown].sort((a, b) => {
    if (sortMode === 'newest') return new Date(b.call_started_at) - new Date(a.call_started_at);
    if (sortMode === 'oldest') return new Date(a.call_started_at) - new Date(b.call_started_at);
    // recommended: urgency first, then NEW_SEEKER, then newest
    const w = (c) => (c.urgency_flag ? 0 : c.call_type === 'NEW_SEEKER' ? 1 : c.call_type ? 2 : 3);
    return w(a) - w(b) || new Date(b.call_started_at) - new Date(a.call_started_at);
  });
  const totalPages = Math.max(1, Math.ceil(sorted.length / perPage));
  const safePage = Math.min(page, totalPages);
  const pageRows = sorted.slice((safePage - 1) * perPage, safePage * perPage);

  const sources = [...new Set(calls.map(c => c.source).filter(Boolean))].sort();
  const scopedFacilities = scope && !scope.org_wide
    ? facilities.filter(f => scope.facility_ids.includes(f.id))
    : facilities;
  const facilityLocked = scope && !scope.org_wide && scopedFacilities.length <= 1;

  // Triage strip buckets - REAL classifications only, counted from loaded rows
  const bucketCounts = {};
  calls.forEach(c => {
    const k = !c.call_type ? 'unscored'
      : (c.call_type === 'JUNK' || c.call_type === 'TOO_SHORT') ? 'LOW'
      : (c.call_type === 'CURRENT_CLIENT' || c.call_type === 'CURRENT_CLIENT_FAMILY') ? 'EXISTING'
      : c.call_type;
    bucketCounts[k] = (bucketCounts[k] || 0) + 1;
  });
  const STRIP = [
    { key: 'NEW_SEEKER', label: 'Likely Admission', sub: 'High intent signals', color: T.blue },
    { key: 'EXISTING', label: 'Existing Clients', sub: 'Known contacts', color: T.amber },
    { key: 'OTHER_BUSINESS', label: 'Business / Referral', sub: 'Providers, professionals', color: '#6D28D9' },
    { key: 'LOW', label: 'Low Priority', sub: 'Wrong numbers, spam', color: T.sub },
    { key: 'unscored', label: 'Awaiting AI', sub: 'Not yet scored', color: '#94A3B8' },
  ].filter(b => bucketCounts[b.key]);

  const TABS = [
    { key: 'all', label: 'All Calls' },
    { key: 'missed', label: 'Missed' },
    { key: 'needs_triage', label: 'Needs Triage' },
    { key: 'likely_junk', label: 'Likely Junk' },
    { key: 'qualified', label: 'Qualified' },
    { key: 'dismissed', label: 'Dismissed' },
    { key: 'ai_review', label: 'AI Review' },
  ];

  const STATS = [
    ['Calls Today', today.calls_today ?? '-', T.text],
    ['Qualified Today', today.qualified_today ?? '-', T.green],
    ['Needs Review', (counts.needs_triage || 0) + (reviewCount || 0), T.amber],
    ['Missed Today', today.missed_today ?? '-', (today.missed_today > 0 ? T.red : T.text)],
    ['Urgent Today', today.urgent_today ?? '-', (today.urgent_today > 0 ? T.red : T.text)],
  ];

  const toggleExpand = (id) => {
    setDismissOpen(false); setQualifyOpen(false); setShowFullT(false);
    setExpanded(expanded === id ? null : id);
  };

  const expandedCall = calls.find(c => c.id === expanded);

  return (
    <div style={s.wrap}>
      <div style={s.head}>
        <h1 style={s.h1}>Calls</h1>
        <p style={s.sub}>Every inbound call. Qualify the real ones, dismiss the rest.</p>
      </div>

      {/* Summary strip */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(5,1fr)', gap: 10, marginBottom: 16 }}>
        {STATS.map(([l, v, c]) => (
          <div key={l} style={s.stat}>
            <div style={s.statLabel}>{l}</div>
            <div style={{ ...s.statVal, color: c }}>{v}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={s.tabs}>
        {TABS.map(t => {
          const active = tab === t.key;
          const n = t.key === 'ai_review' ? reviewCount : (counts[t.key] || 0);
          return (
            <button key={t.key} onClick={() => setTab(t.key)} style={{
              ...s.tab,
              borderBottom: active ? '2px solid ' + T.blue : '2px solid transparent',
              color: active ? T.blue : T.sub, fontWeight: active ? 700 : 500,
            }}>
              {t.label}
              <span style={{ ...s.badge, background: active ? T.blue : '#E8EFF6', color: active ? '#fff' : T.sub }}>{n}</span>
            </button>
          );
        })}
      </div>

      {/* Triage strip - real buckets only */}
      {tab === 'needs_triage' && STRIP.length > 0 && (
        <div style={s.strip}>
          <div style={{ minWidth: 120 }}>
            <div style={{ fontSize: 10.5, fontWeight: 800, color: T.sub, letterSpacing: '0.06em' }}>TCIO TRIAGE</div>
            <div style={{ fontSize: 15, fontWeight: 800, color: T.text }}>{calls.length} Need Review</div>
          </div>
          {STRIP.map(b => {
            const on = clsFilter === b.key;
            return (
              <button key={b.key} onClick={() => { setClsFilter(on ? 'all' : b.key); setPage(1); }}
                style={{ ...s.stripItem, borderColor: on ? b.color : 'transparent', background: on ? '#F4F8FB' : 'transparent' }}>
                <span style={{ fontSize: 16, fontWeight: 800, color: b.color }}>{bucketCounts[b.key]}</span>
                <span style={{ fontSize: 12.5, fontWeight: 700, color: T.text }}>{b.label}</span>
                <span style={{ fontSize: 11, color: T.sub }}>{b.sub}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Filter row */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: isMobile ? '1 1 100%' : '1 1 220px' }}>
          <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search calls..." style={s.searchInput} />
          {search && <button onClick={() => setSearch('')} style={s.clearBtn}>x</button>}
        </div>
        {facilityLocked ? (
          scopedFacilities[0] && <span style={s.lockedFac}>{scopedFacilities[0].org_name}</span>
        ) : (
          <select value={facFilter} onChange={e => setFacFilter(e.target.value)} style={s.filter}>
            <option value="all">{scope && !scope.org_wide ? 'My Facilities' : 'All Facilities'}</option>
            {scopedFacilities.map(f => <option key={f.id} value={f.id}>{f.org_name}</option>)}
          </select>
        )}
        <select value={srcFilter} onChange={e => { setSrcFilter(e.target.value); setPage(1); }} style={s.filter}>
          <option value="all">Source</option>
          {sources.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
        <select value={dirFilter} onChange={e => { setDirFilter(e.target.value); setPage(1); }} style={s.filter}>
          <option value="all">Direction</option>
          <option value="inbound">Inbound</option>
          <option value="outbound">Outbound</option>
        </select>
        <select value={clsFilter} onChange={e => { setClsFilter(e.target.value); setPage(1); }} style={s.filter}>
          <option value="all">AI Classification</option>
          <option value="NEW_SEEKER">Likely Admission</option>
          <option value="EXISTING">Existing Client / Family</option>
          <option value="OTHER_BUSINESS">Business / Referral</option>
          <option value="LOW">Low Priority</option>
          <option value="unscored">Awaiting AI</option>
        </select>
        <select value={dateFilter} onChange={e => { setDateFilter(e.target.value); setPage(1); }} style={s.filter}>
          <option value="all">Any date</option>
          <option value="today">Last 24h</option>
          <option value="week">Last 7 days</option>
          <option value="month">Last 30 days</option>
          <option value="year">This Year</option>
        </select>
        {tab === 'needs_triage' && (
          <select value={sortMode} onChange={e => setSortMode(e.target.value)} style={s.filter}>
            <option value="recommended">Recommended</option>
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
          </select>
        )}
      </div>

      {/* Cross-tab search hits */}
      {search.trim().length >= 3 && xhits.filter(h => h.status !== tab).length > 0 && (
        <div style={s.xhits}>
          <div style={s.xhitsTitle}>Also found in other sections - tap to jump</div>
          {xhits.filter(h => h.status !== tab).slice(0, 8).map(h => {
            const p = statusPill(h.status);
            return (
              <div key={h.id} onClick={() => setTab(h.status)} style={s.xhitRow}>
                <span style={{ ...s.pill, background: p.bg, color: p.fg }}>{p.label}</span>
                <span style={{ fontWeight: 600, fontSize: 13 }}>{h.caller_name || h.caller_phone}</span>
                <span style={{ color: T.sub, fontSize: 12.5 }}>{h.caller_phone}{h.caller_city ? ' - ' + h.caller_city : ''}{h.source ? ' - ' + h.source : ''}</span>
              </div>
            );
          })}
        </div>
      )}

      {tab === 'missed' ? <MissedCalls headers={headers} search={search} facFilter={facFilter} />
      : tab === 'ai_review' ? <AIReview headers={headers} facFilter={facFilter} />
      : loading ? <div style={s.empty}>Loading...</div>
      : sorted.length === 0 ? <div style={s.empty}>Nothing here.</div>
      : (
        <div style={{ background: T.surface, border: '1px solid ' + T.border, borderRadius: 8, overflow: 'hidden' }}>
          {!isMobile && (
            <div style={s.thead}>
              <span style={{ flex: '1.6 1 0' }}>CALLER</span>
              <span style={{ flex: '1.2 1 0' }}>SOURCE</span>
              <span style={{ flex: '0.9 1 0' }}>AI / HISTORY</span>
              <span style={{ flex: '0.8 1 0' }}>TIME</span>
              <span style={{ flex: '0.6 1 0' }}>DURATION</span>
              <span style={{ flex: '1.4 1 0' }}>AI CLASSIFICATION</span>
              <span style={{ flex: '0.9 1 0' }}>STATUS</span>
              <span style={{ flex: '0.7 1 0', textAlign: 'right' }}>ACTION</span>
            </div>
          )}
          {pageRows.map(c => {
            const ci = clsInfo(c);
            const p = statusPill(c.status);
            const isOpen = expanded === c.id;
            const ex = (() => {
              if (!c.ai_extracted) return {};
              if (typeof c.ai_extracted === 'string') { try { return JSON.parse(c.ai_extracted); } catch (e) { return {}; } }
              return c.ai_extracted;
            })();
            const priorN = Number(c.prior_calls || 0);
            return (
              <div key={c.id} style={{ borderBottom: '1px solid #EDF2F7' }}>
                {isMobile ? (
                  <div onClick={() => toggleExpand(c.id)} style={{ ...s.mCard, background: isOpen ? '#F8FBFE' : T.surface }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: 700, fontSize: 14.5, color: T.text }}>{c.caller_phone}</span>
                      <span style={{ ...s.pill, background: p.bg, color: p.fg }}>{p.label}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 5, flexWrap: 'wrap', alignItems: 'center' }}>
                      {c.urgency_flag && <span style={{ ...s.pill, background: '#FEE2E2', color: T.red }}>URGENT</span>}
                      <span style={{ fontSize: 12, fontWeight: 700, color: ci.color }}>{ci.label}</span>
                      <span style={{ fontSize: 12, color: T.sub }}>{fmtTime(c.call_started_at)} - {fmtDuration(c.duration_seconds)}</span>
                      {priorN > 0 && <span style={{ fontSize: 11.5, color: T.amber, fontWeight: 600 }}>{priorN} prior</span>}
                    </div>
                  </div>
                ) : (
                  <div onClick={() => toggleExpand(c.id)} style={{ ...s.row, background: isOpen ? '#F8FBFE' : T.surface }}>
                    <span style={{ flex: '1.6 1 0', minWidth: 0 }}>
                      <span style={{ fontWeight: 700, fontSize: 13.5, color: T.text, display: 'block' }}>{c.caller_phone}</span>
                      <span style={{ fontSize: 12, color: T.sub }}>{c.caller_city ? c.caller_city + ', ' + (c.caller_state || '') : ''}</span>
                    </span>
                    <span style={{ flex: '1.2 1 0', minWidth: 0, fontSize: 12.5, color: T.text }}>
                      <span style={{ display: 'block', fontWeight: 600 }}>{c.direction === 'outbound' ? 'Outbound' : c.source || 'Unknown'}</span>
                      <span style={{ fontSize: 11.5, color: T.sub }}>{c.facility_name || c.company_name || ''}</span>
                    </span>
                    <span style={{ flex: '0.9 1 0' }}>
                      {priorN > 0
                        ? <span style={{ ...s.pill, background: '#FEF3C7', color: T.amber }}>{priorN} prior call{priorN > 1 ? 's' : ''}</span>
                        : <span style={{ fontSize: 12, color: '#94A3B8' }}>-</span>}
                    </span>
                    <span style={{ flex: '0.8 1 0', fontSize: 12.5, color: T.sub }}>
                      <span style={{ display: 'block', color: T.text }}>{fmtTime(c.call_started_at)}</span>
                      <span style={{ fontSize: 11.5 }}>{fmtClock(c.call_started_at)}</span>
                    </span>
                    <span style={{ flex: '0.6 1 0', fontSize: 12.5, color: T.text, fontWeight: 600 }}>{fmtDuration(c.duration_seconds)}</span>
                    <span style={{ flex: '1.4 1 0', minWidth: 0 }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {c.urgency_flag && <span style={{ ...s.pill, background: '#FEE2E2', color: T.red }}>URGENT</span>}
                        <span style={{ fontSize: 12.5, fontWeight: 700, color: ci.color }}>{ci.label}</span>
                      </span>
                      {c.status === 'qualified' && c.lead_name &&
                        <span style={{ fontSize: 11.5, color: T.sub }}>{c.lead_name}{c.lead_stage ? ' - ' + c.lead_stage : ''}</span>}
                    </span>
                    <span style={{ flex: '0.9 1 0' }}>
                      <span style={{ ...s.pill, background: p.bg, color: p.fg }}>{p.label}</span>
                    </span>
                    <span style={{ flex: '0.7 1 0', textAlign: 'right' }}>
                      <button onClick={(e) => { e.stopPropagation(); toggleExpand(c.id); }} style={s.reviewBtn}>
                        {isOpen ? 'Close' : 'Review'}
                      </button>
                    </span>
                  </div>
                )}

                {/* ---- Inline expand ---- */}
                {isOpen && (
                  <div style={s.expand}>
                    <div style={s.expandHead}>
                      <span style={{ fontWeight: 800, fontSize: 14.5, color: T.text }}>{c.caller_phone}</span>
                      {c.caller_city && <span style={{ color: T.sub, fontSize: 13 }}>{c.caller_city}, {c.caller_state}</span>}
                      <span style={{ color: T.sub, fontSize: 13 }}>{c.direction === 'outbound' ? 'Outbound' : 'Inbound'}{c.facility_name ? ' - ' + c.facility_name : ''}</span>
                      <span style={{ ...s.pill, background: '#EAF3FE', color: T.blue }}>{fmtDuration(c.duration_seconds)}</span>
                      {priorN > 0 && <span style={{ ...s.pill, background: '#FEF3C7', color: T.amber }}>{priorN} prior calls</span>}
                      <span style={{ color: T.sub, fontSize: 12.5, marginLeft: 'auto' }}>{fmtTime(c.call_started_at)} ({fmtClock(c.call_started_at)})</span>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.3fr 1.5fr 1fr', gap: 14, marginTop: 12 }}>
                      {/* AI intelligence - real fields only, visually marked as AI */}
                      <div>
                        <div style={s.secTitle}>AI CALL SUMMARY</div>
                        {c.ai_summary
                          ? <div style={{ fontSize: 13, color: T.text, lineHeight: 1.5 }}>{c.ai_summary}</div>
                          : <div style={{ fontSize: 12.5, color: '#94A3B8' }}>Not yet scored by AI.</div>}
                        {(ex.primary_substance || ex.payer_name || ex.how_soon || ex.caller_relationship || ex.member_id) && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                            {ex.primary_substance && <span style={s.aiChip}>AI - Substance: {ex.primary_substance}</span>}
                            {ex.payer_name && <span style={s.aiChip}>AI - Insurance: {ex.payer_name}</span>}
                            {ex.member_id && <span style={s.aiChip}>AI - Member ID: {ex.member_id}</span>}
                            {ex.how_soon && <span style={s.aiChip}>AI - How soon: {ex.how_soon}</span>}
                            {ex.caller_relationship && <span style={s.aiChip}>AI - Caller: {ex.caller_relationship}</span>}
                          </div>
                        )}
                        {c.rep_name && <div style={{ fontSize: 12, color: T.sub, marginTop: 8 }}>Rep: {c.rep_name}</div>}
                      </div>

                      {/* Transcript + audio */}
                      <div>
                        <div style={s.secTitle}>TRANSCRIPT</div>
                        {showFullT && fullT[c.id] ? (
                          <div style={{ ...s.transcript, maxHeight: 260, overflow: 'auto', whiteSpace: 'pre-wrap' }}>{fullT[c.id]}</div>
                        ) : c.transcript_preview ? (
                          <div style={s.transcript}>"{c.transcript_preview}..."</div>
                        ) : (
                          <div style={{ fontSize: 12.5, color: '#94A3B8' }}>No transcript yet.</div>
                        )}
                        {c.transcript_preview && (
                          <button onClick={() => loadFullTranscript(c)} style={s.linkBtn}>
                            {showFullT && fullT[c.id] ? 'Hide Full Transcript' : 'View Full Transcript'}
                          </button>
                        )}
                        {c.recording_url && (
                          <div style={{ marginTop: 10 }}>
                            <button onClick={() => playRecording(c)} style={s.btnGhost}>{playing === c.id ? 'Stop Audio' : 'Play Audio'}</button>
                            {playing === c.id && c._audio && <audio controls autoPlay src={c._audio} style={{ width: '100%', marginTop: 8 }} />}
                          </div>
                        )}
                      </div>

                      {/* Lead + actions */}
                      <div>
                        <div style={s.secTitle}>LEAD</div>
                        {(c.qualified_lead_id || c.matched_lead_id) && c.lead_name ? (
                          <div style={{ fontSize: 13 }}>
                            <div style={{ fontWeight: 700, color: T.text }}>{c.lead_name}</div>
                            {c.lead_stage && <div style={{ color: T.sub, fontSize: 12.5, marginTop: 2 }}>{c.lead_stage}</div>}
                          </div>
                        ) : (
                          (c.status === 'needs_triage' || c.status === 'likely_junk') ? <LeadMatches call={c} api={API} headers={headers} btn={s.btnSecondary} onDone={() => { setExpanded(null); load(); }} /> : <div style={{ fontSize: 12.5, color: '#94A3B8' }}>No lead linked yet.</div>
                        )}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
                          {(c.status === 'needs_triage' || c.status === 'likely_junk') && (
                            <>
                              <button onClick={() => { setQualifyOpen(!qualifyOpen); setDismissOpen(false); }} style={s.btnPrimary}>Qualify as Lead</button>
                              <button onClick={() => { setDismissOpen(!dismissOpen); setQualifyOpen(false); }} style={s.btnSecondary}>Dismiss</button>
                            </>
                          )}
                          {c.status === 'dismissed' && (
                            <>
                              <div style={{ fontSize: 12.5, color: T.red }}>Dismissed - {c.dismissed_reason}</div>
                              <button onClick={() => doUndismiss(c)} style={s.btnSecondary}>Undo Dismiss</button>
                            </>
                          )}
                          {c.status === 'qualified' && (
                            <span style={{ ...s.pill, background: '#DCFCE7', color: '#166534', alignSelf: 'flex-start' }}>{c.routed_action === 'lead_updated' ? 'Lead updated' : (c.routed_action === 'lead_created' || c.routed_action === 'alumni_lead_created') ? 'Lead created' : c.qualified_lead_id ? 'Lead linked' : 'Qualified'}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {dismissOpen && (
                      <div style={s.panel}>
                        <div style={s.panelTitle}>Why dismiss?</div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          {DISMISS_REASONS.map(r => (
                            <button key={r} disabled={busy} onClick={() => doDismiss(c, r)} style={s.reason}>{r}</button>
                          ))}
                          <button onClick={() => setDismissOpen(false)} style={s.btnGhost}>Cancel</button>
                        </div>
                      </div>
                    )}
                    {qualifyOpen && (
                      <QualifyPanel call={c} facilities={scopedFacilities} busy={busy}
                        onCancel={() => setQualifyOpen(false)} onConfirm={(fid, loc) => doQualify(c, fid, loc)} />
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Pagination */}
          <div style={s.pager}>
            <span style={{ fontSize: 12.5, color: T.sub }}>
              Showing {sorted.length === 0 ? 0 : (safePage - 1) * perPage + 1}-{Math.min(safePage * perPage, sorted.length)} of {sorted.length} calls
            </span>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <select value={perPage} onChange={e => { setPerPage(Number(e.target.value)); setPage(1); }} style={{ ...s.filter, minWidth: 0 }}>
                {[10, 25, 50].map(n => <option key={n} value={n}>{n} per page</option>)}
              </select>
              <button disabled={safePage <= 1} onClick={() => setPage(safePage - 1)} style={s.pageBtn}>Prev</button>
              <span style={{ fontSize: 12.5, color: T.text, fontWeight: 700 }}>{safePage} / {totalPages}</span>
              <button disabled={safePage >= totalPages} onClick={() => setPage(safePage + 1)} style={s.pageBtn}>Next</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function QualifyPanel({ call, facilities, busy, onCancel, onConfirm }) {
  const [facilityId, setFacilityId] = useState(call.facility_id || '');
  const [loc, setLoc] = useState('');
  return (
    <div style={{ ...s.panel, background: '#F2FBF5', borderColor: '#BBE7C8' }}>
      <div style={s.panelTitle}>Create lead from this call</div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div>
          <label style={s.label}>Facility</label>
          <select value={facilityId} onChange={e => setFacilityId(e.target.value)} style={s.input}>
            <option value="">Select...</option>
            {facilities.map(f => <option key={f.id} value={f.id}>{f.org_name}</option>)}
          </select>
        </div>
        <div>
          <label style={s.label}>Level of Care</label>
          <select value={loc} onChange={e => setLoc(e.target.value)} style={s.input}>
            <option value="">Unknown</option>
            {['DTX', 'RTC', 'PHP', 'IOP'].map(l => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
        <button disabled={busy || !facilityId} onClick={() => onConfirm(facilityId, loc)}
          style={{ ...s.btnPrimary, opacity: (busy || !facilityId) ? 0.5 : 1 }}>
          {busy ? 'Creating...' : 'Create Lead'}
        </button>
        <button onClick={onCancel} style={s.btnGhost}>Cancel</button>
      </div>
    </div>
  );
}

function MissedCalls({ headers, search, facFilter }) {
  const [rows, setRows] = React.useState([]);
  const [stats, setStats] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(null);
  const [showHandled, setShowHandled] = React.useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const fq = facFilter && facFilter !== 'all' ? '&facilityId=' + facFilter : '';
      const r = await fetch(API + '/api/calls/missed?days=14' + fq, { headers });
      const j = await r.json();
      if (j.ok) { setRows(j.data); setStats(j.stats); }
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  React.useEffect(() => { load(); /* eslint-disable-next-line */ }, [facFilter]);

  const callBack = async (row) => {
    setBusy(row.id);
    try {
      const r = await fetch(API + '/api/call/dial', {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ lead_id: row.lead_id || null, phone: row.caller_phone }),
      });
      const j = await r.json();
      if (j.ok) { window.alert('Calling you now - answer and CallRail connects you to ' + (row.known_name || row.caller_phone)); setTimeout(load, 3000); }
      else window.alert(j.message || j.error || 'Dial failed');
    } catch (e) { window.alert('Network error'); }
    setBusy(null);
  };

  const dismiss = async (row, reason) => {
    setBusy(row.id);
    try {
      const r = await fetch(API + '/api/calls/' + row.id + '/dismiss-missed', {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      });
      if (r.status === 401) alert('Your session expired - please log in again.');
      else { const d = await r.json().catch(() => ({})); if (!r.ok || d.ok === false) alert('Dismiss failed: ' + (d.error || r.status)); }
      await load();
    } catch (e) { alert('Dismiss failed: ' + e.message); }
    setBusy(null);
  };

  const cbOutcome = async (row, outcome) => {
    let note;
    if (outcome === 'spoke_to') {
      note = window.prompt('Quick note on the conversation (so nobody double-calls them):');
      if (note === null) return;
    }
    setBusy(row.id);
    try {
      const r = await fetch(API + '/api/calls/' + row.id + '/callback-outcome', {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ outcome, note }),
      });
      if (r.status === 401) alert('Your session expired - please log in again.');
      else { const d = await r.json().catch(() => ({})); if (!r.ok || d.ok === false) alert('Failed: ' + (d.error || r.status)); }
      await load();
    } catch (e) { alert('Failed: ' + e.message); }
    setBusy(null);
  };

  const [mQualify, setMQualify] = React.useState(null);
  const [mFacilities, setMFacilities] = React.useState([]);
  React.useEffect(() => { (async () => {
    try {
      const r = await fetch(API + '/api/facilities', { headers });
      const j = await r.json();
      setMFacilities(j.data || j || []);
    } catch (e) {}
  })(); /* eslint-disable-next-line */ }, []);
  const mDoQualify = async (row, facilityId, loc) => {
    setBusy(row.id);
    try {
      const r = await fetch(API + '/api/calls/' + row.id + '/qualify', {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ facility_id: facilityId, loc_requested: loc }),
      });
      const d = await r.json().catch(() => ({}));
      if (r.status === 401) alert('Your session expired - please log in again.');
      else if (!r.ok || d.ok === false) alert('Qualify failed: ' + (d.error || r.status));
      else setMQualify(null);
      await load();
    } catch (e) { alert('Qualify failed: ' + e.message); }
    setBusy(null);
  };

  const ago = (ts) => {
    const mins = Math.round((Date.now() - new Date(ts).getTime()) / 60000);
    if (mins < 60) return mins + 'm ago';
    if (mins < 1440) return Math.round(mins / 60) + 'h ago';
    return Math.round(mins / 1440) + 'd ago';
  };
  const recTime = (secs) => secs < 3600 ? Math.round(secs / 60) + ' min' : Math.round(secs / 3600) + ' hrs';

  const _mq = (search || '').toLowerCase();
  const _mqd = _mq.replace(/[^0-9]/g, '');
  const matches = r => !_mq || [r.known_name, r.caller_name, r.caller_city, r.source].some(f => (f || '').toLowerCase().includes(_mq)) || (_mqd.length >= 3 && (r.caller_phone || '').replace(/[^0-9]/g, '').includes(_mqd));
  const open = rows.filter(r => !r.recovered_at && !r.dismissed_at && !r.last_callback_outcome).filter(matches);
  const handled = rows.filter(r => (r.recovered_at || r.dismissed_at || r.last_callback_outcome)).filter(matches);

  if (loading) return <div style={s.empty}>Loading missed calls...</div>;
  return (
    <div>
      {stats && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
          {[['Open (needs callback)', stats.open, T.red], ['Recovered', stats.recovered, T.green],
            ['Recovery rate', stats.recovery_rate + '%', T.blue],
            ['Avg time to recover', stats.avg_recovery_mins != null ? stats.avg_recovery_mins + ' min' : '-', T.blue]].map(([l, v, c2]) => (
            <div key={l} style={{ ...s.stat, minWidth: 140 }}>
              <div style={s.statLabel}>{l}</div>
              <div style={{ ...s.statVal, color: c2 }}>{v}</div>
            </div>
          ))}
        </div>
      )}
      {open.length === 0 && <div style={{ background: '#F2FBF5', border: '1px solid #BBE7C8', borderRadius: 8, padding: 14, color: T.green, fontWeight: 600, marginBottom: 14, fontSize: 13.5 }}>All missed calls handled. Nothing to chase.</div>}
      {open.map(r => (
        <div key={r.id} style={{ background: T.surface, border: '1px solid ' + T.border, borderLeft: '3px solid ' + T.red, borderRadius: 8, padding: '10px 14px', marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: T.text }}>
              {r.known_name || r.caller_name || 'Unknown caller'}
              <span style={{ color: T.sub, fontWeight: 500, marginLeft: 8, fontSize: 13 }}>{r.caller_phone}</span>
              {r.lead_id && <span style={{ ...s.pill, background: '#EAF3FE', color: T.blue, marginLeft: 8 }}>HAS LEAD{r.pipeline_stage ? ' - ' + r.pipeline_stage : ''}</span>}
              {r.miss_count > 1 && <span style={{ ...s.pill, background: '#FEF3C7', color: T.amber, marginLeft: 6 }}>missed {r.miss_count}x</span>}
            </div>
            <div style={{ fontSize: 12, color: T.sub, marginTop: 2 }}>
              Missed {ago(r.call_started_at)}{r.caller_city ? ' - ' + r.caller_city + ', ' + (r.caller_state || '') : ''}{r.source ? ' - ' + r.source : ''}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            {r.last_callback_outcome && (
              <span style={{ ...s.pill, background: '#FEF3C7', color: T.amber }}>
                {({ voicemail: 'Left VM', no_answer: 'No answer', spoke_to: 'Spoke to' })[r.last_callback_outcome] || r.last_callback_outcome} - {ago(r.last_callback_at)}{r.last_callback_note ? ' - ' + r.last_callback_note : ''}</span>
            )}
            <button disabled={busy === r.id} onClick={() => cbOutcome(r, 'no_answer')} style={s.btnSecondary}>No answer</button>
            <button disabled={busy === r.id} onClick={() => cbOutcome(r, 'voicemail')} style={s.btnSecondary}>Left VM</button>
            <button disabled={busy === r.id} onClick={() => cbOutcome(r, 'spoke_to')} style={{ ...s.btnSecondary, borderColor: '#9CC7F4', color: T.blue }}>Spoke to</button>
            <button disabled={busy === r.id} onClick={() => setMQualify(mQualify === r.id ? null : r.id)} style={{ ...s.btnSecondary, borderColor: '#BBE7C8', color: T.green }}>Qualify</button>
            <button disabled={busy === r.id} onClick={() => callBack(r)}
              style={{ ...s.btnPrimary, opacity: busy === r.id ? 0.6 : 1 }}>
              {busy === r.id ? 'Dialing...' : 'Call Back'}</button>
            <select defaultValue="" disabled={busy === r.id} onChange={e => { if (e.target.value) dismiss(r, e.target.value); }} style={s.filter}>
              <option value="" disabled>Dismiss...</option>
              <option value="junk">Junk / robocall</option>
              <option value="wrong_number">Wrong number</option>
              <option value="vendor">Vendor / business</option>
              <option value="spam">Spam</option>
              <option value="other">Other</option>
            </select>
          </div>
          {mQualify === r.id && (
            <div style={{ width: '100%' }}>
              <QualifyPanel call={{ id: r.id, facility_id: '' }} facilities={mFacilities} busy={busy === r.id}
                onCancel={() => setMQualify(null)} onConfirm={(fid, loc) => mDoQualify(r, fid, loc)} />
            </div>
          )}
        </div>
      ))}
      <div style={{ marginTop: 16 }}>
        <button onClick={() => setShowHandled(!showHandled)} style={{ ...s.linkBtn, marginTop: 0 }}>
          {showHandled ? 'Hide' : 'Show'} Handled ({handled.length})
        </button>
        {showHandled && handled.map(r => (
          <div key={r.id} style={{ background: '#FAFCFE', border: '1px solid ' + T.border, borderRadius: 8, padding: '9px 14px', marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <span style={{ fontWeight: 600, fontSize: 13, color: T.text }}>{r.known_name || r.caller_name || r.caller_phone}</span>
              <span style={{ color: '#94A3B8', marginLeft: 8, fontSize: 12 }}>{r.caller_phone} - missed {ago(r.call_started_at)}</span>
            </div>
            <div style={{ fontSize: 12, fontWeight: 700 }}>
              {r.recovered_at
                ? <span style={{ color: T.green }}>Recovered in {recTime(r.recovered_secs)}</span>
                : <span style={{ color: '#94A3B8' }}>Dismissed - {r.dismissed_reason}
                    <button onClick={async () => { await fetch(API + '/api/calls/' + r.id + '/undismiss-missed', { method: 'POST', headers }); load(); }}
                      style={{ ...s.btnGhost, marginLeft: 8, padding: '2px 8px', fontSize: 11 }}>Undo</button>
                  </span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AIReview({ headers, facFilter }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openT, setOpenT] = useState(null);
  const [attaching, setAttaching] = useState(null);
  const [creating, setCreating] = useState(null);
  const [q, setQ] = useState('');
  const [results, setResults] = useState({ leads: [], alumni: [] });
  const [nameForm, setNameForm] = useState({ first_name: '', last_name: '' });

  const load = async () => {
    setLoading(true);
    const fq = facFilter && facFilter !== 'all' ? '?facilityId=' + facFilter : '';
    const r = await fetch(API + '/api/calls/review' + fq, { headers });
    const d = await r.json();
    if (d.ok) setItems(d.data);
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [facFilter]);

  const searchTargets = async (term) => {
    setQ(term);
    if (term.trim().length < 2) { setResults({ leads: [], alumni: [] }); return; }
    const r = await fetch(API + '/api/calls/review-search?q=' + encodeURIComponent(term), { headers });
    const d = await r.json();
    if (d.ok) setResults(d);
  };

  const resolve = async (callrailId, body) => {
    const r = await fetch(API + '/api/calls/review/' + callrailId + '/resolve', {
      method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    const d = await r.json();
    if (!d.ok) { alert('Error: ' + d.error); return; }
    setAttaching(null); setCreating(null); setQ(''); setResults({ leads: [], alumni: [] });
    load();
  };

  if (loading) return <div style={s.empty}>Loading...</div>;
  if (items.length === 0) return <div style={s.empty}>Review queue is clear.</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {items.map(c => (
        <div key={c.callrail_id} style={{ background: T.surface, border: '1px solid ' + T.border, borderLeft: '3px solid #6D28D9', borderRadius: 8, padding: 14 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ ...s.pill, background: '#F1EBFD', color: '#6D28D9' }}>{c.call_type}</span>
            <span style={{ fontWeight: 700, fontSize: 14, color: T.text }}>{c.customer_phone}</span>
            <span style={{ color: T.sub, fontSize: 12.5 }}>{fmtTime(c.call_started_at)} - {fmtDuration(c.duration_seconds)}</span>
            {c.rep_name && <span style={{ color: T.sub, fontSize: 12.5 }}>rep: {c.rep_name}</span>}
          </div>
          <div style={{ marginTop: 6, fontSize: 13, color: T.text, lineHeight: 1.45 }}>{c.summary}</div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            <button onClick={() => { setAttaching(attaching === c.callrail_id ? null : c.callrail_id); setCreating(null); setQ(''); setResults({ leads: [], alumni: [] }); }} style={s.btnPrimary}>Attach to existing</button>
            <button onClick={() => { setCreating(creating === c.callrail_id ? null : c.callrail_id); setAttaching(null); setNameForm({ first_name: '', last_name: '' }); }} style={s.btnSecondary}>Create lead</button>
            <button onClick={() => { if (window.confirm('Dismiss this call from review?')) resolve(c.callrail_id, { action: 'dismiss' }); }} style={s.btnGhost}>Dismiss</button>
            {c.transcript && <button onClick={() => setOpenT(openT === c.callrail_id ? null : c.callrail_id)} style={s.btnGhost}>{openT === c.callrail_id ? 'Hide transcript' : 'Transcript'}</button>}
          </div>
          {attaching === c.callrail_id && (
            <div style={s.panel}>
              <div style={s.panelTitle}>Attach this call to...</div>
              <input autoFocus value={q} onChange={e => searchTargets(e.target.value)}
                placeholder="Search by name or phone - leads and alumni"
                style={{ ...s.input, width: '100%', boxSizing: 'border-box', marginBottom: 10 }} />
              {results.leads.map(l => (
                <div key={l.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #EDF2F7' }}>
                  <span style={{ fontSize: 13 }}><strong>{l.first_name} {l.last_name}</strong> - {l.phone} - <span style={{ color: T.sub }}>{l.pipeline_stage} (lead)</span></span>
                  <button onClick={() => resolve(c.callrail_id, { action: 'attach', lead_id: l.id })} style={s.btnPrimary}>Attach</button>
                </div>
              ))}
              {results.alumni.map(a => (
                <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #EDF2F7' }}>
                  <span style={{ fontSize: 13 }}><strong>{a.first_name} {a.last_name}</strong> - {a.phone} - <span style={{ color: T.sub }}>alumni {a.loc || ''} (no lead - use Create instead, notes will reference)</span></span>
                </div>
              ))}
              {q.length >= 2 && !results.leads.length && !results.alumni.length && <div style={{ color: T.sub, fontSize: 13 }}>No matches.</div>}
            </div>
          )}
          {creating === c.callrail_id && (
            <div style={{ ...s.panel, background: '#F2FBF5', borderColor: '#BBE7C8' }}>
              <div style={s.panelTitle}>Create a lead from this call</div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div><label style={s.label}>First name</label>
                  <input value={nameForm.first_name} onChange={e => setNameForm(f => ({ ...f, first_name: e.target.value }))} style={s.input} /></div>
                <div><label style={s.label}>Last name</label>
                  <input value={nameForm.last_name} onChange={e => setNameForm(f => ({ ...f, last_name: e.target.value }))} style={s.input} /></div>
                <button onClick={() => resolve(c.callrail_id, { action: 'create', ...nameForm })} style={s.btnPrimary}>Create Lead</button>
              </div>
            </div>
          )}
          {openT === c.callrail_id && (
            <div style={{ ...s.transcript, maxHeight: 300, overflow: 'auto', whiteSpace: 'pre-wrap', fontStyle: 'normal' }}>{c.transcript}</div>
          )}
        </div>
      ))}
    </div>
  );
}

const s = {
  wrap: { padding: 20, maxWidth: 1240, margin: '0 auto' },
  head: { marginBottom: 16 },
  h1: { fontSize: 22, fontWeight: 800, color: T.text, margin: 0 },
  sub: { color: T.sub, margin: '3px 0 0', fontSize: 13.5 },
  stat: { background: T.surface, borderRadius: 8, padding: '10px 14px', border: '1px solid ' + T.border },
  statLabel: { fontSize: 10.5, color: T.sub, textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.04em' },
  statVal: { fontSize: 21, fontWeight: 800, marginTop: 2 },
  tabs: { display: 'flex', gap: 2, marginBottom: 12, borderBottom: '1px solid ' + T.border, overflowX: 'auto' },
  tab: { background: 'none', border: 'none', padding: '9px 13px', cursor: 'pointer', fontSize: 13.5,
         display: 'flex', alignItems: 'center', gap: 7, marginBottom: -1, whiteSpace: 'nowrap' },
  badge: { fontSize: 11, fontWeight: 700, borderRadius: 9, padding: '1px 7px' },
  strip: { display: 'flex', gap: 14, alignItems: 'center', background: T.surface, border: '1px solid ' + T.border,
           borderRadius: 8, padding: '10px 16px', marginBottom: 12, overflowX: 'auto' },
  stripItem: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1, cursor: 'pointer',
               background: 'transparent', border: '1px solid transparent', borderRadius: 8, padding: '6px 12px', whiteSpace: 'nowrap' },
  searchInput: { width: '100%', padding: '8px 30px 8px 11px', border: '1px solid ' + T.border, borderRadius: 7,
                 fontSize: 13, boxSizing: 'border-box', color: T.text, background: T.surface },
  clearBtn: { position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', border: 'none',
              background: 'none', cursor: 'pointer', color: '#94A3B8', fontSize: 13, fontWeight: 700 },
  filter: { padding: '8px 10px', border: '1px solid ' + T.border, borderRadius: 7, fontSize: 12.5,
            background: T.surface, color: T.text, cursor: 'pointer' },
  lockedFac: { padding: '8px 12px', border: '1px solid ' + T.border, borderRadius: 7, fontSize: 12.5,
               background: '#F4F8FB', color: T.sub, fontWeight: 600 },
  xhits: { background: '#FAFCFE', border: '1px solid ' + T.border, borderRadius: 8, padding: '10px 14px', marginBottom: 12 },
  xhitsTitle: { fontSize: 10.5, fontWeight: 800, color: T.sub, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 },
  xhitRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '6px 4px', cursor: 'pointer',
             borderBottom: '1px solid #EDF2F7', flexWrap: 'wrap' },
  thead: { display: 'flex', gap: 8, padding: '9px 16px', background: '#FAFCFE', borderBottom: '1px solid ' + T.border,
           fontSize: 10.5, fontWeight: 800, color: T.sub, letterSpacing: '0.05em' },
  row: { display: 'flex', gap: 8, padding: '11px 16px', alignItems: 'center', cursor: 'pointer', minHeight: 44 },
  mCard: { padding: '12px 14px', cursor: 'pointer' },
  reviewBtn: { background: T.surface, color: T.blue, border: '1px solid #9CC7F4', borderRadius: 6,
               padding: '6px 14px', fontWeight: 700, fontSize: 12.5, cursor: 'pointer' },
  expand: { background: T.surface, borderTop: '2px solid ' + T.blue, padding: '14px 18px' },
  expandHead: { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', paddingBottom: 10,
                borderBottom: '1px solid ' + T.border },
  secTitle: { fontSize: 10.5, fontWeight: 800, color: T.sub, letterSpacing: '0.06em', marginBottom: 7 },
  aiChip: { fontSize: 11.5, fontWeight: 600, background: '#F1EBFD', color: '#6D28D9', borderRadius: 6, padding: '3px 9px' },
  pill: { fontSize: 11, fontWeight: 700, borderRadius: 6, padding: '2px 8px', whiteSpace: 'nowrap' },
  transcript: { fontSize: 12.5, color: T.text, background: '#FAFCFE', border: '1px solid ' + T.border,
                borderLeft: '3px solid ' + T.border, padding: '8px 12px', borderRadius: 6, lineHeight: 1.5 },
  linkBtn: { background: 'none', border: 'none', color: T.blue, fontWeight: 700, fontSize: 12.5,
             cursor: 'pointer', padding: 0, marginTop: 8, display: 'block' },
  btnPrimary: { background: T.blue, color: '#fff', border: 'none', borderRadius: 7, padding: '8px 16px',
                fontWeight: 700, fontSize: 12.5, cursor: 'pointer' },
  btnSecondary: { background: T.surface, color: T.text, border: '1px solid ' + T.border, borderRadius: 7,
                  padding: '8px 14px', fontWeight: 600, fontSize: 12.5, cursor: 'pointer' },
  btnGhost: { background: 'none', color: T.sub, border: '1px solid ' + T.border, borderRadius: 7,
              padding: '7px 12px', fontSize: 12.5, cursor: 'pointer' },
  panel: { marginTop: 14, padding: 14, background: T.surface, border: '1px solid ' + T.border, borderRadius: 8 },
  panelTitle: { fontSize: 11, fontWeight: 800, color: T.sub, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 },
  reason: { background: T.surface, border: '1px solid ' + T.border, borderRadius: 6, padding: '6px 12px',
            fontSize: 12.5, cursor: 'pointer', color: T.text },
  label: { display: 'block', fontSize: 10.5, fontWeight: 700, color: T.sub, textTransform: 'uppercase', marginBottom: 4 },
  input: { padding: '8px 10px', borderRadius: 6, border: '1px solid ' + T.border, fontSize: 13, minWidth: 160,
           background: T.surface, color: T.text },
  pager: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px',
           background: '#FAFCFE', borderTop: '1px solid ' + T.border, flexWrap: 'wrap', gap: 8 },
  pageBtn: { background: T.surface, border: '1px solid ' + T.border, borderRadius: 6, padding: '6px 12px',
             fontSize: 12.5, fontWeight: 600, color: T.text, cursor: 'pointer' },
  empty: { padding: 50, textAlign: 'center', color: '#94A3B8', fontSize: 13.5 },
};

// Leads that share this call's phone number, each with a button to attach the call to it.
function LeadMatches({ call, api, headers, btn, onDone }) {
  const [state, setState] = useState(null);
  const [working, setWorking] = useState(false);
  useEffect(() => {
    let live = true;
    fetch(api + '/api/calls/' + call.id + '/lead-matches', { headers })
      .then(r => r.json()).then(d => { if (live) setState(d && d.ok ? d : { matches: [] }); })
      .catch(() => { if (live) setState({ matches: [] }); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [call.id]);
  const attach = async (m) => {
    setWorking(true);
    try {
      const r = await fetch(api + '/api/calls/' + call.id + '/attach', { method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify({ lead_id: m.id }) });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.ok) onDone(); else alert('Could not attach: ' + (d.error || r.status));
    } catch (e) { alert('Could not attach: ' + e.message); }
    setWorking(false);
  };
  const quiet = { fontSize: 12.5, color: '#94A3B8' };
  if (!state) return <div style={quiet}>Looking for leads with this number...</div>;
  if (!state.matches.length) return <div style={quiet}>No lead has this phone number.</div>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ fontSize: 12.5, color: '#64748B' }}>{(() => { const n = new Set(state.matches.map(x => x.contact_id || x.id)).size; return state.matches.length === 1 ? 'One lead has this phone number:' : n === 1 ? 'One client with ' + state.matches.length + ' episodes has this phone number:' : state.matches.length + ' leads for ' + n + ' clients share this phone number:'; })()}</div>
      {state.matches.map(m => (
        <div key={m.id} style={{ border: '1px solid #E2E8F0', borderRadius: 8, padding: '8px 10px' }}>
          <div style={{ fontSize: 13, fontWeight: 700 }}>{m.name}{state.matches.filter(x => x.contact_id && x.contact_id === m.contact_id).length > 1 ? <span style={{ fontWeight: 400, color: '#64748B' }}> · same client, one of their episodes</span> : null}</div>
          <div style={{ fontSize: 12, color: '#64748B', marginBottom: 6 }}>{[String(m.stage || '').replace(/_/g, ' '), m.facility_name].filter(Boolean).join(' · ')}</div>
          {state.can_attach && <button disabled={working} onClick={() => attach(m)} style={{ ...btn, width: '100%' }}>Attach to {m.name}</button>}
        </div>
      ))}
    </div>
  );
}
